/** @jsx jsx */
import React, { useContext } from 'react';
import { jsx, Button, Box, Input, Flex } from 'theme-ui';
import { useState } from 'react';
import { RegionConfigContext } from '../../utilities/RegionConfigContext';

const InlineEmailSignup = ({ 
  heading = 'Subscribe to our newsletter',
  subtext = 'Stay updated with our latest news and offers',
  buttonText = 'Subscribe',
  buttonVariant = 'primary',
  placeholderText = 'Enter your email address',
  pageVariant = '',
  apiEndpoint = '',
  redirectUrl = '',
  regionConfig: regionConfigProp,
  builderBlock 
}) => {
  const regionConfigFromContext = useContext(RegionConfigContext);
  const regionConfig = regionConfigProp ?? regionConfigFromContext;
  const effectiveApiEndpoint = apiEndpoint || regionConfig?.zapierEndpoint || '';
  const [email, setEmail] = useState('');
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Function to get URL query parameters
  const getUrlParams = () => {
    if (typeof window === 'undefined') return {};
    
    const urlParams = new URLSearchParams(window.location.search);
    return {
      utm_campaign: urlParams.get('utm_campaign') || '',
      utm_source: urlParams.get('utm_source') || '',
      utm_medium: urlParams.get('utm_medium') || '',
      utm_content: urlParams.get('utm_content') || '',
    };
  };

  // Function to format current timestamp
  const getCurrentTimestamp = () => {
    const now = new Date();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    const year = now.getFullYear();
    const hours = String(now.getHours()).padStart(2, '0');
    const minutes = String(now.getMinutes()).padStart(2, '0');
    const seconds = String(now.getSeconds()).padStart(2, '0');
    
    return `${month}/${day}/${year} ${hours}:${minutes}:${seconds}`;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!email || isSubmitting) return;
    
    setIsSubmitting(true);
    
    // Show confirmation message
    setShowConfirmation(true);
    
    // Send GTM event
    if (typeof window !== 'undefined' && window.dataLayer) {
      window.dataLayer.push({event: "labs_email_submitted"});
    }
    
    // Prepare data for API request
    const urlParams = getUrlParams();
    const timestamp = getCurrentTimestamp();
    
    // Get page variant from prop, or try to get from global variable set by template
    let currentPageVariant = pageVariant || (typeof window !== 'undefined' ? window.__builderPageUrl : undefined);
    
    const requestData = {
      email: email,
      date: timestamp,
      utm_campaign: urlParams.utm_campaign,
      utm_source: urlParams.utm_source,
      utm_medium: urlParams.utm_medium,
      utm_content: urlParams.utm_content,
      page_variant: currentPageVariant,
    };
    
    // Make POST request if API endpoint is provided
    if (effectiveApiEndpoint) {
      try {
        const response = await fetch(effectiveApiEndpoint, {
          method: 'POST',
          body: JSON.stringify(requestData),
        });
        
        if (!response.ok) {
          console.error('API request failed:', response.status, response.statusText);
        }
      } catch (error) {
        console.error('Error submitting to API:', error);
      }
    }
    
    // Redirect after 1 second if URL is provided
    if (redirectUrl && redirectUrl.trim() !== '') {
      setTimeout(() => {
        window.location.href = redirectUrl;
      }, 1000);
    }
  };

  if (showConfirmation) {
    return (
      <Box
        sx={{
          textAlign: 'center',
          padding: '32px 0',
        }}
      >
        <Box
          sx={{
            width: '64px',
            height: '64px',
            borderRadius: '50%',
            border: '2px solid',
            borderColor: 'primary',
            backgroundColor: 'background',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 24px',
            animation: 'pulse 0.6s ease-in-out',
            '@keyframes pulse': {
              '0%': { transform: 'scale(0.8)', opacity: 0.8 },
              '50%': { transform: 'scale(1.1)', opacity: 1 },
              '100%': { transform: 'scale(1)', opacity: 1 },
            },
          }}
        >
          <Box
            sx={{
              fontSize: '28px',
              color: 'primary',
              fontWeight: 'bold',
            }}
          >
            ✓
          </Box>
        </Box>
        <Box
          as="h3"
          sx={{
            margin: 0,
            fontSize: '20px',
            fontWeight: '600',
            color: 'text',
            lineHeight: '1.4',
            marginBottom: '12px',
          }}
        >
          Thank you for subscribing!
        </Box>
        <Box
          sx={{
            fontSize: '14px',
            color: 'text',
            opacity: 0.7,
            fontWeight: '400',
          }}
        >
          We'll be in touch soon
        </Box>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        textAlign: 'center',
        padding: '32px 0',
      }}
    >
      {heading && (
        <Box
          as="h3"
          sx={{
            margin: 0,
            marginBottom: '12px',
            fontSize: '24px',
            fontWeight: '600',
            color: 'text',
            lineHeight: '1.3',
          }}
        >
          {heading}
        </Box>
      )}
      
      {subtext && (
        <Box
          sx={{
            marginBottom: '24px',
            fontSize: '16px',
            color: 'text',
            opacity: 0.8,
            lineHeight: '1.5',
          }}
        >
          {subtext}
        </Box>
      )}
      
      <form onSubmit={handleSubmit}>
        <Flex
          sx={{
            flexDirection: ['column', 'row'],
            gap: '12px',
            justifyContent: 'center',
            alignItems: ['stretch', 'stretch'],
            maxWidth: '500px',
            margin: '0 auto',
            padding: ['0 20px', '0'],
          }}
        >
          <Input
            type="email"
            placeholder={placeholderText}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            disabled={isSubmitting}
            sx={{
              flex: ['1 1 auto', '1 1 auto'],
              width: ['100%', 'auto'],
              minWidth: ['100%', '200px'],
              padding: '14px 20px',
              border: '2px solid',
              borderColor: 'rgba(0, 0, 0, 0.1)',
              borderRadius: '8px',
              fontSize: '15px',
              backgroundColor: 'background',
              color: 'text',
              transition: 'all 0.2s ease',
              height: ['auto', '100%'],
              boxSizing: 'border-box',
              '&:focus': {
                outline: 'none',
                borderColor: 'primary',
                boxShadow: '0 0 0 3px rgba(33, 150, 243, 0.1)',
              },
              '&:disabled': {
                opacity: 0.6,
                cursor: 'not-allowed',
              },
              '&::placeholder': {
                color: 'text',
                opacity: 0.5,
              },
            }}
          />
          <Button
            type="submit"
            variant={buttonVariant}
            disabled={isSubmitting}
            sx={{
              textTransform: 'uppercase',
              padding: '14px 24px',
              flexShrink: 0,
              width: 'auto',
              alignSelf: ['center', 'stretch'],
              height: ['auto', '100%'],
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              '&:disabled': {
                opacity: 0.6,
                cursor: 'not-allowed',
              },
            }}
          >
            {isSubmitting ? 'Submitting...' : buttonText}
          </Button>
        </Flex>
      </form>
    </Box>
  );
};

export default InlineEmailSignup;

